import React, { useState, useEffect, useRef } from 'react';
import {
  ColorInput,
  EnhancedSlider,
  GridDropdown,
  GridTextInput,
  ToggleCheckbox,
} from '../../utils/gridUtils';

import { useDispatch, useSelector } from 'react-redux';
import { updateLight } from '../../redux/Slice/gridSlice';
import { lightTypeHasHelper } from '../../utils/ThreeJsUtils/lightHelperUtils';

const SHADOW_OPTIONS = [
  { label: 'No Shadow', value: 'No Shadow' },
  { label: 'Soft Shadow', value: 'Soft Shadow' },
  { label: 'Hard Shadow', value: 'Hard Shadow' },
];

const getDefaultIntensity = (type) => {
  switch (type) {
    case 'Spot Light':
    case 'Point Light':
      return 40;
    case 'Area Light':
      return 10;
    case 'Directional Light':
      return 2;
    default:
      return 1;
  }
};

const LightComponent = ({ light, historyUiRevision = 0 }) => {
  const dispatch = useDispatch();
  const selectedLight = light;
  const lastTyped = useRef(0);
  const lastLightId = useRef(light?.id);
  const skipWriteBackUntilRef = useRef(0);

  // Get all lights from Redux to check for duplicates on type change
  const light_data = useSelector((state) => state.assets_object.light_data);

  const [value, setValue] = useState(
    selectedLight?.intensity ??
      getDefaultIntensity(selectedLight?.light_type || 'Directional Light')
  );
  const [isChecked, setIsChecked] = useState(selectedLight?.hall ?? false);
  const [lightType, setLightType] = useState(
    selectedLight?.light_type || 'Directional Light'
  );
  const [color, setColor] = useState(selectedLight?.color || '#ffffff');
  const [shadow, setShadow] = useState(selectedLight?.shadow || 'Soft Shadow');
  const [getShadow, setGetShadow] = useState(selectedLight?.getShadow ?? true);
  const [range, setRange] = useState(selectedLight?.range ?? 100);
  const [shadowRadius, setShadowRadius] = useState(
    selectedLight?.shadowRadius ?? 4
  );
  const [shadowBlur, setShadowBlur] = useState(
    selectedLight?.shadowBlur ?? 3.5
  );
  const [shadowDarkness, setShadowDarkness] = useState(
    selectedLight?.shadowDarkness ?? 1
  );
  const [shadowOpacity, setShadowOpacity] = useState(
    selectedLight?.shadowOpacity ?? 1
  );
  const [angle, setAngle] = useState(
    selectedLight?.angle !== undefined
      ? ((selectedLight.angle * 180) / Math.PI) * 2
      : 60
  );
  const [width, setWidth] = useState(selectedLight?.width ?? 50);
  const [height, setHeight] = useState(selectedLight?.height ?? 50);
  const [helperVisible, setHelperVisible] = useState(
    selectedLight?.helperVisible === true
  );
  const [intensityError, setIntensityError] = useState('');
  const [rangeError, setRangeError] = useState('');
  const [userChangedType, setUserChangedType] = useState(false);

  // Dynamic max intensity
  const maxIntensity =
    lightType === 'Spot Light' || lightType === 'Point Light' ? 100000 : 500;

  // Sync when selected light changes or undo/redo restores light properties
  useEffect(() => {
    const lightChanged = lastLightId.current !== selectedLight?.id;
    if (lightChanged) {
      lastTyped.current = 0;
      lastLightId.current = selectedLight?.id;
      setIntensityError('');
      setRangeError('');
    } else if (historyUiRevision === 0 && Date.now() - lastTyped.current < 600) {
      return;
    } else {
      lastTyped.current = 0;
    }

    // Prevent write-back effect from pushing pre-undo values into Redux.
    if (historyUiRevision > 0) {
      skipWriteBackUntilRef.current = Date.now() + 250;
    }

    setValue((prev) =>
      prev === '' && selectedLight?.intensity === 0
        ? ''
        : (selectedLight?.intensity ??
          getDefaultIntensity(selectedLight?.light_type || 'Directional Light'))
    );
    setIsChecked(selectedLight?.hall ?? false);
    setLightType(selectedLight?.light_type || 'Directional Light');
    setColor(selectedLight?.color || '#ffffff');
    setShadow(selectedLight?.shadow || 'Soft Shadow');
    setGetShadow(selectedLight?.getShadow ?? true);
    setRange((prev) =>
      prev === '' && selectedLight?.range === 0
        ? ''
        : (selectedLight?.range ?? 100)
    );
    setShadowRadius(selectedLight?.shadowRadius ?? 4);
    setShadowBlur(selectedLight?.shadowBlur ?? 3.5);
    setShadowDarkness(selectedLight?.shadowDarkness ?? 1);
    setShadowOpacity(selectedLight?.shadowOpacity ?? 1);
    setAngle(
      selectedLight?.angle !== undefined
        ? ((selectedLight.angle * 180) / Math.PI) * 2
        : 60
    );
    setWidth((prev) =>
      prev === '' && selectedLight?.width === 0
        ? ''
        : (selectedLight?.width ?? 50)
    );
    setHeight((prev) =>
      prev === '' && selectedLight?.height === 0
        ? ''
        : (selectedLight?.height ?? 50)
    );
    setHelperVisible(selectedLight?.helperVisible === true);
    setUserChangedType(false);
  }, [selectedLight, historyUiRevision]);

  // Clamp intensity when type changes
  useEffect(() => {
    if (value > maxIntensity) {
      setValue(maxIntensity);
      setIntensityError(`Max intensity allowed is ${maxIntensity}`);
    } else {
      setIntensityError('');
    }
  }, [lightType]);

  // Generate incremented name when user changes type
  const resolveNameOnTypeChange = (newType) => {
    // Get all lights of the new type excluding the current light
    const sameTypeLights = light_data.filter(
      (l) => l.light_type === newType && l.id !== selectedLight.id
    );

    if (sameTypeLights.length === 0) {
      // No other lights of this type — use plain name
      return newType;
    }

    // Find the highest number suffix already used
    const maxNum = sameTypeLights.reduce((max, l) => {
      const match = l.name.match(/(\d+)$/);
      return match ? Math.max(max, parseInt(match[1])) : Math.max(max, 1);
    }, 1);

    return `${newType} ${maxNum + 1}`;
  };

  // Redux update (debounced)
  useEffect(() => {
    if (!selectedLight) return;
    if (Date.now() < skipWriteBackUntilRef.current) return;

    // Create the updated light object
    const resolvedName = userChangedType
      ? resolveNameOnTypeChange(lightType)
      : selectedLight?.name;

    const updatedLight = {
      ...selectedLight,
      intensity: Number(value),
      hall: isChecked,
      light_type: lightType,
      color,
      shadow,
      getShadow,
      range:
        lightType === 'Spot Light' ||
        lightType === 'Point Light' ||
        lightType === 'Area Light'
          ? Number(range)
          : undefined,
      shadowRadius: Number(shadowRadius),
      shadowBlur: Number(shadowBlur),
      shadowDarkness: Number(shadowDarkness),
      shadowOpacity: Number(shadowOpacity),
      ...(lightType === 'Spot Light'
        ? { angle: ((Number(angle) / 2) * Math.PI) / 180 }
        : {}),
      width: lightType === 'Area Light' ? Number(width) : undefined,
      height: lightType === 'Area Light' ? Number(height) : undefined,
      helperVisible: lightTypeHasHelper(lightType) ? helperVisible : undefined,
      name: resolvedName,
    };

    // Deep equality check to prevent infinite loop
    const hasChanged =
      updatedLight.intensity !== selectedLight.intensity ||
      updatedLight.hall !== selectedLight.hall ||
      updatedLight.light_type !== selectedLight.light_type ||
      updatedLight.color !== selectedLight.color ||
      updatedLight.shadow !== selectedLight.shadow ||
      updatedLight.getShadow !== (selectedLight.getShadow ?? true) ||
      updatedLight.range !== selectedLight.range ||
      updatedLight.shadowRadius !== selectedLight.shadowRadius ||
      updatedLight.shadowBlur !== selectedLight.shadowBlur ||
      updatedLight.shadowDarkness !== selectedLight.shadowDarkness ||
      updatedLight.shadowOpacity !== selectedLight.shadowOpacity ||
      updatedLight.angle !== selectedLight.angle ||
      updatedLight.width !== selectedLight.width ||
      updatedLight.height !== selectedLight.height ||
      updatedLight.name !== selectedLight.name ||
      (lightTypeHasHelper(lightType) &&
        updatedLight.helperVisible !== (selectedLight.helperVisible === true));

    if (!hasChanged) return;

    const handler = setTimeout(() => {
      dispatch(updateLight(updatedLight));
    }, 100);

    return () => clearTimeout(handler);
  }, [
    value,
    isChecked,
    lightType,
    color,
    shadow,
    getShadow,
    range,
    shadowRadius,
    shadowBlur,
    shadowDarkness,
    shadowOpacity,
    angle,
    width,
    height,
    helperVisible,
    userChangedType,
    selectedLight,
    dispatch,
  ]);

  const shadowSettings = getShadow ? (
    <>
      <GridDropdown
        label="Shadow Type"
        value={shadow}
        onChange={(e) => setShadow(e.target.value)}
        options={SHADOW_OPTIONS}
      />
      {shadow === 'Soft Shadow' ? (
        <div className="flex flex-col gap-1 mt-1">
          <EnhancedSlider
            label="Shadow Blur"
            value={shadowBlur}
            onChange={setShadowBlur}
            min={0}
            max={10}
            step={0.1}
          />
          <EnhancedSlider
            label="Shadow Darkness"
            value={shadowDarkness}
            onChange={setShadowDarkness}
            min={0}
            max={1}
            step={0.05}
          />
          <EnhancedSlider
            label="Shadow Opacity"
            value={shadowOpacity}
            onChange={setShadowOpacity}
            min={0}
            max={1}
            step={0.05}
          />
          <EnhancedSlider
            label="Shadow Radius"
            value={shadowRadius}
            onChange={setShadowRadius}
            min={0}
            max={20}
            step={0.5}
          />
        </div>
      ) : null}
    </>
  ) : null;

  // Reusable Range Input
  const renderRangeInput = () => (
    <>
      <GridTextInput
        label="Range"
        value={range}
        type="number"
        min={0}
        maxValue={100}
        onMaxReached={() => {
          setRangeError('Maximum range is 100');
          setTimeout(() => setRangeError(''), 3000);
        }}
        onChange={(e) => {
          lastTyped.current = Date.now();
          setRange(e.target.value);
        }}
      />
      {rangeError && (
        <span className="text-[10px] text-red-500">{rangeError}</span>
      )}
    </>
  );

  const getShadowCheckbox = (
    <ToggleCheckbox
      label="Get Shadow"
      checked={!!getShadow}
      onToggle={() => setGetShadow(!getShadow)}
    />
  );

  const helperVisibleCheckbox = lightTypeHasHelper(lightType) ? (
    <ToggleCheckbox
      label="Light Helper Visible"
      checked={helperVisible}
      onToggle={() => setHelperVisible(!helperVisible)}
    />
  ) : null;

  return (
    <div className="flex flex-col gap-1 pt-2">
      <span className="font-normal text-[12px]">Lights</span>

      <GridDropdown
        label="Type"
        value={lightType}
        menuClassName="homepage-scrollbar"
        onChange={(e) => {
          setLightType(e.target.value);
          setUserChangedType(true); // flag that user changed the type
        }}
        options={[
          { label: 'Point Light', value: 'Point Light' },
          { label: 'Directional Light', value: 'Directional Light' },
          { label: 'Spot Light', value: 'Spot Light' },
          { label: 'Area Light', value: 'Area Light' },
          { label: 'Ambient Light', value: 'Ambient Light' },
        ]}
      />

      <ColorInput color={color} setColor={setColor} />

      <GridTextInput
        label="Intensity"
        value={value}
        type="number"
        min={0}
        maxValue={maxIntensity}
        step={0.1}
        onMaxReached={() => {
          setIntensityError(`Max intensity allowed is ${maxIntensity}`);
          setTimeout(() => setIntensityError(''), 3000);
        }}
        onChange={(e) => {
          lastTyped.current = Date.now();
          setValue(e.target.value);
        }}
      />

      {intensityError && (
        <span className="text-[10px] text-red-500">{intensityError}</span>
      )}

      {helperVisibleCheckbox}

      {/* Spot Light */}
      {lightType === 'Spot Light' && (
        <>
          {renderRangeInput()}
          <EnhancedSlider
            label="Spot Angle"
            value={angle}
            onChange={setAngle}
            min={1}
            max={180}
            step={1}
            unit="°"
          />
          {getShadowCheckbox}
          {shadowSettings}
        </>
      )}

      {/* Directional Light */}
      {lightType === 'Directional Light' && (
        <>
          {getShadowCheckbox}
          {shadowSettings}
        </>
      )}
      {/* Point Light */}
      {lightType === 'Point Light' && (
        <>
          {renderRangeInput()}
          {getShadowCheckbox}
          {shadowSettings}
        </>
      )}

      {/* Area Light */}
      {lightType === 'Area Light' && (
        <>
          {renderRangeInput()}
          <EnhancedSlider label="Width" value={width} onChange={setWidth} />
          <EnhancedSlider label="Height" value={height} onChange={setHeight} />
          {getShadowCheckbox}
          {shadowSettings}
        </>
      )}
    </div>
  );
};

export default LightComponent;
